/**
 * obstacles.ts — Destructible circular obstacle terrain logic
 *
 * Obstacles are circular entities placed on the game field.
 * When hit by a projectile trajectory, a bite hole is carved out at the impact point.
 */

import { FieldBounds, TrajectoryPoint, DEFAULT_FIELD_BOUNDS } from './trajectory';

export type ObstacleDensity = 'none' | 'low' | 'medium' | 'high';

/** A carved bite hole out of an obstacle */
export interface ObstacleBite {
  x: number;
  y: number;
  radius: number;
}

/** A circular obstacle entity */
export interface Obstacle {
  id: string;
  x: number;
  y: number;
  radius: number;
  bites: ObstacleBite[];
}

/** Result of checking trajectory collision with obstacles */
export interface ObstacleHitResult {
  hitPoint: TrajectoryPoint;
  hitIndex: number;
  obstacle: Obstacle;
}

/**
 * Finds the two intersection parameters (t values) where segment AB crosses the boundary
 * of a circle centered at (cx,cy) with radius r. Returns sorted t values, or null if no intersection.
 * t ∈ [0,1] means the intersection is on the segment.
 */
function lineCircleIntersections(
  ax: number, ay: number,
  bx: number, by: number,
  cx: number, cy: number,
  r: number,
): [number, number] | null {
  const dx = bx - ax;
  const dy = by - ay;
  const fx = ax - cx;
  const fy = ay - cy;

  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - r * r;

  let discriminant = b * b - 4 * a * c;
  if (discriminant < 0 || a === 0) return null;

  discriminant = Math.sqrt(discriminant);
  const t1 = (-b - discriminant) / (2 * a);
  const t2 = (-b + discriminant) / (2 * a);

  return [t1, t2];
}

/**
 * Checks if a world coordinate (px, py) is inside the solid region of an obstacle
 * (within obstacle radius, but NOT inside any carved bite holes).
 */
export function isPointInObstacle(px: number, py: number, obstacle: Obstacle): boolean {
  const distSq = (px - obstacle.x) * (px - obstacle.x) + (py - obstacle.y) * (py - obstacle.y);
  if (distSq > obstacle.radius * obstacle.radius) {
    return false;
  }

  // Check if point falls inside any carved bite hole
  for (const bite of obstacle.bites) {
    const biteDistSq = (px - bite.x) * (px - bite.x) + (py - bite.y) * (py - bite.y);
    if (biteDistSq <= bite.radius * bite.radius) {
      return false; // Inside a bite hole, so not solid!
    }
  }

  return true;
}
/**
 * Checks if a point is inside the outer circle of an obstacle (ignoring bites).
 */
function isPointInCircle(px: number, py: number, obstacle: Obstacle): boolean {
  const dx = px - obstacle.x;
  const dy = py - obstacle.y;
  return dx * dx + dy * dy <= obstacle.radius * obstacle.radius;
}

/**
 * Finds the exact point where segment (ax,ay)→(bx,by) crosses the circumference of
 * a circle at (cx,cy) with radius r. Returns the ENTRY point (closest to A).
 * Assumes A is outside and B is inside (or crossing through).
 */
function findCircumferenceEntry(
  ax: number, ay: number,
  bx: number, by: number,
  cx: number, cy: number,
  r: number,
): { x: number; y: number } | null {
  const dx = bx - ax;
  const dy = by - ay;
  const fx = ax - cx;
  const fy = ay - cy;

  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - r * r;

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0 || a === 0) return null;

  const sqrtDisc = Math.sqrt(discriminant);
  // t1 is the entry point (closest to A)
  const t1 = (-b - sqrtDisc) / (2 * a);

  // Clamp to segment range
  const t = Math.max(0, Math.min(1, t1));

  return {
    x: ax + t * dx,
    y: ay + t * dy,
  };
}

/**
 * Checks a trajectory against all obstacles.
 * Finds the exact point on the obstacle circumference where the trajectory enters.
 */
export function checkObstacleCollision(
  trajectory: TrajectoryPoint[],
  obstacles: Obstacle[],
  shooterX?: number,
  shooterY?: number,
): ObstacleHitResult | null {
  if (trajectory.length === 0 || obstacles.length === 0) return null;

  // Identify obstacles that the shooter is currently inside/touching
  const shooterObstacleIds = new Set<string>();
  if (shooterX !== undefined && shooterY !== undefined) {
    for (const obs of obstacles) {
      if (isPointInObstacle(shooterX, shooterY, obs)) {
        shooterObstacleIds.add(obs.id);
      }
    }
  }

  for (let i = 0; i < trajectory.length; i++) {
    const pt = trajectory[i];

    for (const obstacle of obstacles) {
      if (shooterObstacleIds.has(obstacle.id)) continue;

      if (isPointInObstacle(pt.x, pt.y, obstacle)) {
        // Backtrack to previous point to find exact entry on boundary
        const prev = i > 0 ? trajectory[i - 1] : pt;

        // Find intersections with all relevant circles to get the exact entry point
        let bestT = 1.0;
        let entryPoint = pt;

        // 1. Outer circle
        const outerTs = lineCircleIntersections(
          prev.x, prev.y, pt.x, pt.y,
          obstacle.x, obstacle.y, obstacle.radius
        );
        if (outerTs) {
          for (const t of outerTs) {
            if (t >= 0 && t <= 1 && t < bestT) {
              bestT = t;
              entryPoint = { x: prev.x + t * (pt.x - prev.x), y: prev.y + t * (pt.y - prev.y) };
            }
          }
        }

        // 2. Bite circles (inner walls)
        for (const bite of obstacle.bites) {
          const biteTs = lineCircleIntersections(
            prev.x, prev.y, pt.x, pt.y,
            bite.x, bite.y, bite.radius
          );
          if (biteTs) {
            for (const t of biteTs) {
              if (t >= 0 && t <= 1 && t < bestT) {
                bestT = t;
                entryPoint = { x: prev.x + t * (pt.x - prev.x), y: prev.y + t * (pt.y - prev.y) };
              }
            }
          }
        }

        return {
          hitPoint: {
            x: Math.round(entryPoint.x * 100) / 100,
            y: Math.round(entryPoint.y * 100) / 100,
          },
          hitIndex: i > 0 ? i - 1 : 0,
          obstacle,
        };
      }
    }
  }

  return null;
}

/**
 * Carves a circular bite hole out of an obstacle at (hitX, hitY).
 * Default biteRadius is 0.65 world units so bites look like clean edge notches.
 */
export function applyBite(
  obstacle: Obstacle,
  hitX: number,
  hitY: number,
  biteRadius: number = 0.65,
): ObstacleBite {
  const bite: ObstacleBite = {
    x: Math.round(hitX * 100) / 100,
    y: Math.round(hitY * 100) / 100,
    radius: biteRadius,
  };
  obstacle.bites.push(bite);
  return bite;
}

/**
 * Generates random circular obstacles on the field avoiding player spawn points.
 */
export function generateObstacles(
  density: ObstacleDensity,
  bounds: FieldBounds = DEFAULT_FIELD_BOUNDS,
  soldierPositions: Array<{ x: number; y: number }> = [],
): Obstacle[] {
  if (density === 'none') return [];

  const counts: Record<ObstacleDensity, number> = {
    none: 0,
    low: 3,
    medium: 6,
    high: 10,
  };

  const targetCount = counts[density] || 0;
  const obstacles: Obstacle[] = [];

  const xMin = bounds.minX + 4;
  const xMax = bounds.maxX - 4;
  const yMin = bounds.minY + 4;
  const yMax = bounds.maxY - 4;

  let attempts = 0;
  while (obstacles.length < targetCount && attempts < 200) {
    attempts++;
    const radius = Math.round((1.8 + Math.random() * 1.7) * 10) / 10; // 1.8 to 3.5 world units
    const x = Math.round((xMin + Math.random() * (xMax - xMin)) * 10) / 10;
    const y = Math.round((yMin + Math.random() * (yMax - yMin)) * 10) / 10;

    // Check distance to soldier positions (must be at least radius + 2.5 world units away)
    const tooCloseToSoldier = soldierPositions.some(
      s => Math.hypot(s.x - x, s.y - y) < radius + 2.5,
    );

    if (!tooCloseToSoldier) {
      obstacles.push({
        id: `obs-${obstacles.length}`,
        x,
        y,
        radius,
        bites: [],
      });
    }
  }

  return obstacles;
}
